import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { Search, CreditCard, TrendingUp, User, Phone, MessageSquare, CheckCircle, Wallet, FileText, Car } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { debtorService, api, verificationService } from '@/lib/api';
import WhatsAppBubble from '@/components/WhatsAppBubble';

// Icon mapping for search screen
const searchIcons = {
  search: Search,
  wallet: Wallet,
  creditcard: CreditCard,
  document: FileText,
  user: User
};

// All possible search methods with their config
const ALL_SEARCH_METHODS = [
  { key: 'dni', settingKey: 'search_by_dni', label: 'DNI', icon: User, placeholder: '12345678', inputMode: 'numeric', pattern: '[0-9]*', maxLength: 8, hint: 'Ingresa solo números (8 dígitos)', labelFull: 'Número de DNI', searchField: 'dni' },
  { key: 'cuit', settingKey: 'search_by_cuit', label: 'CUIT', icon: User, placeholder: '20123456789', inputMode: 'numeric', pattern: '[0-9]*', maxLength: 11, hint: 'Ingresa solo números (11 dígitos)', labelFull: 'Número de CUIT', searchField: 'dni' },
  { key: 'client_code', settingKey: 'search_by_code', label: 'Código', icon: CreditCard, placeholder: 'CLI-001', inputMode: 'text', pattern: undefined, maxLength: undefined, hint: null, labelFull: 'Código de Cliente', searchField: 'client_code' },
  { key: 'plate', settingKey: 'search_by_plate', label: 'Dominio', icon: Car, placeholder: 'AD558AD', inputMode: 'text', pattern: '[A-Za-z0-9]*', maxLength: 7, hint: 'Solo letras y números (máximo 7 caracteres)', labelFull: 'Dominio del Vehículo', searchField: 'plate' },
];

const DebtorSearch = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [tenantSlug, setTenantSlug] = useState(slug || 'demo');
  const [tenantName, setTenantName] = useState('');
  const [tenant, setTenant] = useState(null);
  const [searchType, setSearchType] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [tenantLoading, setTenantLoading] = useState(!!slug);
  
  // Phone verification state
  const [showPhoneVerification, setShowPhoneVerification] = useState(false);
  const [phone, setPhone] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [phoneStep, setPhoneStep] = useState('phone'); // 'phone' or 'code'
  const [foundDebtorToken, setFoundDebtorToken] = useState(null);
  const [countryCode, setCountryCode] = useState('+54 9');
  const [timeLeft, setTimeLeft] = useState(600);
  
  // Online users
  const [onlineUsersCount, setOnlineUsersCount] = useState(null);
  const [showOnlineUsers, setShowOnlineUsers] = useState(false);

  // Compute enabled search methods based on tenant settings
  const enabledMethods = useMemo(() => {
    if (!tenant) return [];
    return ALL_SEARCH_METHODS.filter(m => tenant[m.settingKey]);
  }, [tenant]);

  useEffect(() => {
    // If slug from URL, load tenant info
    if (slug) {
      loadTenantInfo(slug);
    }
    // Detect country by IP
    detectCountry();
  }, [slug]);

  const detectCountry = async () => {
    try {
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      // Set country code based on detected country
      const countryCodes = {
        'AR': '+54 9',
        'MX': '+52',
        'CO': '+57',
        'CL': '+56',
        'PE': '+51',
        'EC': '+593',
        'UY': '+598',
        'PY': '+595',
        'BO': '+591',
        'VE': '+58',
        'BR': '+55',
        'US': '+1',
        'ES': '+34'
      };
      setCountryCode(countryCodes[data.country_code] || '+54 9');
    } catch (error) {
      console.log('Could not detect country, using default +54 9');
      setCountryCode('+54 9');
    }
  };

  const loadTenantInfo = async (tenantSlug) => {
    setTenantLoading(true);
    try {
      const response = await api.get(`/tenant/${tenantSlug}`);
      const data = response.data;
      setTenantName(data.name);
      setTenantSlug(tenantSlug);
      setTenant(data);
      
      // Auto-select first enabled search method
      const firstEnabled = ALL_SEARCH_METHODS.find(m => data[m.settingKey]);
      if (firstEnabled) {
        setSearchType(firstEnabled.key);
      } else {
        // Fallback to DNI if nothing enabled
        setSearchType('dni');
      }
      
      // Set online users
      setShowOnlineUsers(data.show_online_users || false);
      if (data.show_online_users && data.online_users_count) {
        setOnlineUsersCount(data.online_users_count);
      }
    } catch (error) {
      console.error('Error loading tenant:', error);
      setTenantName('MacroPago');
      setSearchType('dni');
    } finally {
      setTenantLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Map search type to the correct API field
      const method = ALL_SEARCH_METHODS.find(m => m.key === searchType);
      const searchField = method?.searchField || searchType;
      const searchData = {
        tenant_slug: tenantSlug,
        [searchField]: searchValue
      };
      
      const response = await debtorService.search(searchData);
      const { access_token } = response.data;
      
      // Check if phone verification is required
      if (tenant?.require_phone_verification) {
        setFoundDebtorToken(access_token);
        setShowPhoneVerification(true);
      } else {
        navigate(`/debtor/${access_token}`);
      }
    } catch (error) {
      toast.error('No se encontró ningún cliente con esos datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    
    // Format phone with country code
    const fullPhone = phone.startsWith('+') ? phone : `${countryCode}${phone}`.replace(/\s/g, '');
    
    // Validate phone format
    const phoneRegex = /^\+?[1-9]\d{1,14}$/;
    if (!phoneRegex.test(fullPhone.replace(/\s/g, ''))) {
      toast.error('Formato de teléfono inválido');
      return;
    }

    setLoading(true);
    try {
      await verificationService.sendCode({
        phone: fullPhone,
        debtor_token: foundDebtorToken
      });
      
      toast.success('Código enviado por WhatsApp');
      setPhoneStep('code');
      
      // Start countdown
      setTimeLeft(600);
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
    } catch (error) {
      let message = 'Error al enviar código';
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail)) {
        message = detail.map(e => e.msg || e).join(', ');
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    
    if (phoneCode.length !== 6) {
      toast.error('El código debe tener 6 dígitos');
      return;
    }

    setLoading(true);
    try {
      await verificationService.verifyCode({
        code: phoneCode,
        debtor_token: foundDebtorToken
      });
      
      toast.success('Teléfono verificado');
      navigate(`/debtor/${foundDebtorToken}`);
      
    } catch (error) {
      let message = 'Código inválido';
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail)) {
        message = detail.map(e => e.msg || e).join(', ');
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Phone verification screen
  if (showPhoneVerification) {
    return (
      <div className="min-h-screen gradient-bg flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="w-full max-w-md"
        >
          <Card className="p-8 border-slate-200 shadow-xl">
            {phoneStep === 'phone' ? (
              <>
                <div 
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
                  style={{backgroundColor: `${tenant?.primary_color || '#0d9488'}20`}}
                >
                  <Phone className="w-8 h-8" style={{color: tenant?.primary_color || '#0d9488'}} />
                </div>
                
                <h2 className="text-2xl font-bold text-slate-900 text-center mb-2" style={{fontFamily: 'Manrope'}}>
                  Verificación de Teléfono
                </h2>
                <p className="text-slate-600 text-center mb-6 text-sm">
                  Para tu seguridad, necesitamos verificar tu número de teléfono
                </p>

                <form onSubmit={handleSendCode} className="space-y-4">
                  <div>
                    <Label htmlFor="phone" className="text-slate-700 font-medium">
                      Número de WhatsApp
                    </Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        value={countryCode}
                        onChange={(e) => setCountryCode(e.target.value)}
                        className="w-24 h-11 text-center"
                        placeholder="+54 9"
                      />
                      <Input
                        id="phone"
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value.replace(/[^\d]/g, ''))}
                        placeholder="11 1234-5678"
                        className="flex-1 h-11"
                        required
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Ingresa tu número sin el 0 ni el 15
                    </p>
                  </div>

                  <Button
                    type="submit"
                    className="w-full text-white h-11 font-semibold"
                    style={{backgroundColor: tenant?.primary_color || '#0d9488'}}
                    disabled={loading || !phone}
                  >
                    {loading ? 'Enviando...' : 'Enviar Código por WhatsApp'}
                    {!loading && <MessageSquare className="ml-2 w-5 h-5" />}
                  </Button>
                </form>

                <button
                  type="button"
                  onClick={() => setShowPhoneVerification(false)}
                  className="w-full mt-4 text-sm text-slate-600 hover:text-slate-900"
                >
                  ← Volver a la búsqueda
                </button>
              </>
            ) : (
              <>
                <div 
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
                  style={{backgroundColor: '#10b98120'}}
                >
                  <MessageSquare className="w-8 h-8 text-emerald-600" />
                </div>
                
                <h2 className="text-2xl font-bold text-slate-900 text-center mb-2" style={{fontFamily: 'Manrope'}}>
                  Ingresa el Código
                </h2>
                <p className="text-slate-600 text-center mb-1 text-sm">
                  Enviamos un código de verificación a:
                </p>
                <p className="text-center mb-6 font-semibold" style={{color: tenant?.primary_color || '#0d9488'}}>
                  {countryCode} {phone}
                </p>

                <form onSubmit={handleVerifyCode} className="space-y-4">
                  <div>
                    <Label htmlFor="code" className="text-slate-700 font-medium">
                      Código de 6 dígitos
                    </Label>
                    <Input
                      id="code"
                      type="text"
                      value={phoneCode}
                      onChange={(e) => setPhoneCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="123456"
                      className="mt-1 h-11 text-center text-2xl tracking-widest font-mono"
                      maxLength={6}
                      required
                      autoFocus
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-emerald-600 text-white hover:bg-emerald-700 h-11 font-semibold"
                    disabled={loading || phoneCode.length !== 6}
                  >
                    {loading ? 'Verificando...' : 'Verificar Código'}
                    {!loading && <CheckCircle className="ml-2 w-5 h-5" />}
                  </Button>
                </form>

                <div className="mt-6 space-y-3">
                  {timeLeft > 0 ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
                      <p className="text-sm text-amber-800">
                        El código expira en: <strong className="font-mono">{formatTime(timeLeft)}</strong>
                      </p>
                    </div>
                  ) : (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                      <p className="text-sm text-red-800 mb-2">
                        El código ha expirado
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setPhoneStep('phone');
                          setPhoneCode('');
                          setTimeLeft(600);
                        }}
                        className="text-xs"
                      >
                        Solicitar Nuevo Código
                      </Button>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => {
                      setPhoneStep('phone');
                      setPhoneCode('');
                      setTimeLeft(600);
                    }}
                    className="w-full text-sm text-slate-600 hover:text-slate-900"
                  >
                    ← Cambiar número de teléfono
                  </button>
                </div>
              </>
            )}
          </Card>
        </motion.div>
      </div>
    );
  }

  if (tenantLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{backgroundColor: '#f8fafc'}}>
        <div className="w-10 h-10 border-4 border-t-slate-400 border-slate-200 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-bg">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-2">
            {tenant?.logo_url ? (
              <img src={tenant.logo_url} alt={tenantName} className="h-10 w-auto" />
            ) : (
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{backgroundColor: tenant?.primary_color || '#0d9488'}}
              >
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
            )}
            <span 
              className="text-xl font-bold" 
              style={{
                fontFamily: 'Manrope',
                color: tenant?.secondary_color || '#0f172a'
              }}
            >
              {tenantName}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-md mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="text-center mb-8">
            {(() => {
              const SearchIcon = searchIcons[tenant?.search_icon] || Search;
              return (
                <div 
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{backgroundColor: tenant?.primary_color || '#0d9488'}}
                >
                  <SearchIcon className="w-8 h-8 text-white" />
                </div>
              );
            })()}
            <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Manrope'}}>
              {tenant?.search_title || 'Consulta tu Deuda'}
            </h1>
            <p className="text-slate-600">
              {tenant?.search_subtitle || 'Ingresa tu DNI o Código de Cliente para ver tus deudas pendientes'}
            </p>
            
            {/* Online Users Indicator */}
            {showOnlineUsers && onlineUsersCount && (
              <div className="flex items-center justify-center gap-2 mt-4 text-slate-600 text-sm">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
                <span>{onlineUsersCount} personas consultando ahora su deuda</span>
              </div>
            )}
          </div>

          <Card className="p-6 border-slate-200 shadow-lg" data-testid="search-card">
            <form onSubmit={handleSearch} className="space-y-6">
              {/* Dynamic Search Type Selector */}
              {enabledMethods.length > 1 && (
                <div className={`grid gap-2`} style={{ gridTemplateColumns: `repeat(${Math.min(enabledMethods.length, 4)}, 1fr)` }}>
                  {enabledMethods.map((method) => {
                    const Icon = method.icon;
                    const isActive = searchType === method.key;
                    return (
                      <button
                        key={method.key}
                        type="button"
                        data-testid={`search-type-${method.key}`}
                        onClick={() => { setSearchType(method.key); setSearchValue(''); }}
                        className={`p-3 border-2 rounded-lg transition-all ${
                          isActive
                            ? 'border-teal-600 bg-teal-50'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <Icon className={`w-5 h-5 mx-auto mb-1 ${
                          isActive ? 'text-teal-600' : 'text-slate-400'
                        }`} />
                        <span className={`text-xs font-medium ${
                          isActive ? 'text-teal-900' : 'text-slate-600'
                        }`}>
                          {method.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Search Input */}
              {(() => {
                const currentMethod = ALL_SEARCH_METHODS.find(m => m.key === searchType);
                if (!currentMethod) return null;
                const isNumeric = currentMethod.inputMode === 'numeric';
                return (
                  <div>
                    <Label htmlFor="search" className="text-slate-700 font-medium">
                      {currentMethod.labelFull}
                    </Label>
                    <Input
                      id="search"
                      data-testid="search-input"
                      type="text"
                      inputMode={currentMethod.inputMode}
                      pattern={currentMethod.pattern}
                      value={searchValue}
                      onChange={(e) => {
                        const val = e.target.value;
                        const max = currentMethod.maxLength;
                        if (isNumeric) {
                          if (/^\d*$/.test(val)) setSearchValue(max ? val.slice(0, max) : val);
                        } else if (searchType === 'plate') {
                          if (/^[A-Za-z0-9]*$/.test(val)) setSearchValue(val.toUpperCase().slice(0, 7));
                        } else {
                          setSearchValue(max ? val.slice(0, max) : val);
                        }
                      }}
                      className="mt-1 h-11"
                      placeholder={currentMethod.placeholder}
                      maxLength={currentMethod.maxLength}
                      required
                    />
                    {currentMethod.hint && (
                      <p className="text-xs text-slate-500 mt-1">{currentMethod.hint}</p>
                    )}
                  </div>
                );
              })()}

              <Button
                data-testid="search-submit-button"
                type="submit"
                className="w-full text-white hover:opacity-90 h-11 font-semibold"
                style={{backgroundColor: tenant?.primary_color || '#0d9488'}}
                disabled={loading}
              >
                {loading ? 'Buscando...' : 'Buscar Deuda'}
                <Search className="ml-2 w-5 h-5" />
              </Button>
            </form>
          </Card>

          <div className="mt-6 text-center">
            <p className="text-sm text-slate-600">
              ¿Eres administrador?{' '}
              <button
                data-testid="admin-login-link"
                onClick={() => navigate('/login')}
                className="text-teal-600 hover:text-teal-700 font-medium"
              >
                Iniciar sesión aquí
              </button>
            </p>
          </div>
        </motion.div>
      </div>
      {tenant?.whatsapp_bubble_enabled && tenant?.whatsapp_bubble_number && (
        <WhatsAppBubble phoneNumber={tenant.whatsapp_bubble_number} message={tenant.whatsapp_bubble_message} />
      )}
    </div>
  );
};

export default DebtorSearch;